import { differenceInDays, format, isSameDay, startOfDay } from 'date-fns';
import { useEffect, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useDispatch, useSelector } from 'react-redux';

import { isNil } from 'lodash-es';
import { useTranslation } from 'react-i18next';
import { useDebounceCallback } from 'usehooks-ts';
import useTimezone from '../../../hooks/useTimezone';
import useUser from '../../../hooks/useUser';
import { setAppointmentOverviewDays } from '../../../redux/ducks/AppointmentDucks';
import { getAppointmentDayDetailsRequest } from '../../../redux/ducks/DashboardDucks';
import type {
  GetAppointmentDayDetailsRequestType,
  GetAppointmentDayDetailsResponseItemType,
} from '../../../redux/ducks/DashboardDucks.types';
import {
  setDashboardCalendarTargetDate,
  setDashboardCalendarVisibleDay,
  setDashboardNewAppointmentModalInputTime,
} from '../../../redux/ducks/UIDucks';
import type { RootReducerType } from '../../../redux/reducers/RootReducer.types';
import Spinner from '../Spinner/Spinner';
import CalendarDayOverviewDayBlock from './CalendarDayOverviewDayBlock';

const DAYS_WINDOW_RADIUS = 7;
const LOAD_MORE_DAYS_AMOUNT = 7;

type DateScrollTo = {
  target: Date;
  behavior: 'auto' | 'instant' | 'smooth';
};

const CalendarDayOverview = () => {
  const dispatch = useDispatch();
  const { currentTimezone } = useTimezone();
  const { currentUser } = useUser();
  const { t } = useTranslation();
  const daysListRef = useRef<any>(null);

  const { dashboardCalendarTargetDate, dashboardCalendarVisibleDay } =
    useSelector((state: RootReducerType) => state.ui);
  const { getAppointmentDayDetailsResponse } = useSelector(
    (state: RootReducerType) => state.dashboard,
  );
  const { appointmentsOverviewDays } = useSelector(
    (state: RootReducerType) => state.appointment,
  );

  const [fromDate, setFromDate] = useState<Date | undefined>();
  const [untilDate, setUntilDate] = useState<Date | undefined>();
  const [fetching, setFetching] = useState<boolean>(false);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [dateToScrollTo, setDateToScrollTo] = useState<
    DateScrollTo | undefined
  >();
  const [startEndHours] = useState<number[]>([0, 24]);

  const debounceVisibleDay = useDebounceCallback((day: number) => {
    dispatch(setDashboardCalendarVisibleDay(day));
  }, 250);

  const fetchData = ({
    isLoadBefore,
    forceRefresh,
  }: {
    isLoadBefore: boolean;
    forceRefresh?: boolean;
  }) => {
    if (!forceRefresh && !isNil(untilDate)) {
      if (isLoadBefore && fromDate) {
        const to = fromDate;
        const from = new Date(
          to.getTime() - 1000 * 60 * 60 * 24 * LOAD_MORE_DAYS_AMOUNT,
        );

        const request: GetAppointmentDayDetailsRequestType = {
          from: from.getTime(),
          to: to.getTime(),
          timezone: currentTimezone,
          userPartner: currentUser?.partner_id || '',
        };

        if (!fromDate || from.getTime() < fromDate.getTime()) {
          setFromDate(from);
        }
        if (!untilDate || to.getTime() > untilDate.getTime()) {
          setUntilDate(to);
        }
        dispatch(getAppointmentDayDetailsRequest(request));
      } else {
        const from = untilDate;
        const to = new Date(
          from.getTime() + 1000 * 60 * 60 * 24 * 2 * DAYS_WINDOW_RADIUS,
        );

        const request: GetAppointmentDayDetailsRequestType = {
          from: from.getTime(),
          to: to.getTime(),
          timezone: currentTimezone,
          userPartner: currentUser?.partner_id || '',
        };

        if (!fromDate || from.getTime() < fromDate.getTime()) {
          setFromDate(from);
        }
        if (!untilDate || to.getTime() > untilDate.getTime()) {
          setUntilDate(to);
        }
        dispatch(getAppointmentDayDetailsRequest(request));
      }
    } else {
      const center = startOfDay(
        dashboardCalendarTargetDate
          ? new Date(dashboardCalendarTargetDate)
          : dashboardCalendarVisibleDay
            ? new Date(dashboardCalendarVisibleDay)
            : new Date(),
      );

      const from = new Date(
        center.getTime() - 1000 * 60 * 60 * 24 * DAYS_WINDOW_RADIUS,
      );
      const to = new Date(
        center.getTime() + 1000 * 60 * 60 * 24 * DAYS_WINDOW_RADIUS,
      );

      const request: GetAppointmentDayDetailsRequestType = {
        from: from.getTime(),
        to: to.getTime(),
        timezone: currentTimezone,
        userPartner: currentUser?.partner_id || '',
      };

      setFromDate(from);
      setUntilDate(to);
      dispatch(getAppointmentDayDetailsRequest(request));
    }

    setFetching(true);
  };

  useEffect(() => {
    if (getAppointmentDayDetailsResponse?.data && fetching) {
      const newCalDays: GetAppointmentDayDetailsResponseItemType[] = [];

      let currentDay = getAppointmentDayDetailsResponse.data.from;
      while (currentDay <= getAppointmentDayDetailsResponse.data.to) {
        const date = new Date(currentDay);
        const found = getAppointmentDayDetailsResponse.data.schedule.filter(
          (x) => isSameDay(new Date(x.timestamp), date),
        );

        if (found.length > 0) {
          newCalDays.push(found[0]);
        } else {
          newCalDays.push({
            day: format(new Date(currentDay), 'yyyyMMdd'),
            timestamp: currentDay,
            appointments: [],
          });
        }

        currentDay += 1000 * 60 * 60 * 24;
      }

      const filtered = [...newCalDays, ...appointmentsOverviewDays]
        .reduce(
          (
            unique: GetAppointmentDayDetailsResponseItemType[],
            item: GetAppointmentDayDetailsResponseItemType,
          ): GetAppointmentDayDetailsResponseItemType[] => {
            const existingUnique: GetAppointmentDayDetailsResponseItemType[] =
              unique.filter((x) => x.day !== item.day);

            if (existingUnique.length === unique.length) {
              return [...unique, item];
            }

            return [...unique];
          },
          [],
        )
        .sort((a, b) => a.timestamp - b.timestamp);

      dispatch(setAppointmentOverviewDays(filtered));
      setFetching(false);
      if (isLoadingMore) {
        setIsLoadingMore(false);
      }
    }
  }, [getAppointmentDayDetailsResponse]);

  useEffect(() => {
    dispatch(setDashboardCalendarVisibleDay(null));
    fetchData({ isLoadBefore: false });
    const targetDate = dashboardCalendarTargetDate
      ? new Date(dashboardCalendarTargetDate)
      : dashboardCalendarVisibleDay
        ? new Date(dashboardCalendarVisibleDay)
        : new Date();
    setDateToScrollTo({
      target: targetDate,
      behavior: 'smooth',
    });
    dispatch(setDashboardNewAppointmentModalInputTime(null));

    return () => {
      setFetching(false);
      setIsLoadingMore(false);
      setUntilDate(undefined);
      setFromDate(undefined);
      dispatch(setAppointmentOverviewDays([]));
    };
  }, []);

  useEffect(() => {
    if (!isNil(dashboardCalendarTargetDate)) {
      setUntilDate(undefined);
      dispatch(setAppointmentOverviewDays([]));
      setDateToScrollTo({
        target: new Date(dashboardCalendarTargetDate),
        behavior: 'smooth',
      });
      fetchData({ isLoadBefore: false, forceRefresh: true });
    }
  }, [dashboardCalendarTargetDate]);

  const onLoadMore = () => {
    if (fromDate) {
      setIsLoadingMore(true);
      fetchData({ isLoadBefore: true });
    } else {
      console.error('Cannot load more with no from date');
    }
  };

  const scrollToDate = (
    behavior: 'auto' | 'instant' | 'smooth',
    target: Date,
  ) => {
    if (fromDate && !isNil(daysListRef?.current)) {
      const offsetDays = differenceInDays(target, fromDate);

      const element: Element = daysListRef.current.children[offsetDays];

      if (!isNil(element)) {
        element.scrollIntoView({
          block: 'center',
          inline: 'center',
          behavior,
        });
      }
    } else {
      console.error('Scrolling with no from name is impossible');
    }
  };

  useEffect(() => {
    if (dateToScrollTo && appointmentsOverviewDays.length > 0) {
      scrollToDate(dateToScrollTo.behavior, dateToScrollTo.target);
      setDateToScrollTo(undefined);
      dispatch(setDashboardCalendarTargetDate(null));
    }
  }, [appointmentsOverviewDays]);

  const onNext = () => {
    fetchData({ isLoadBefore: false });
  };

  return (
    <div className="relative flex grow flex-col">
      {appointmentsOverviewDays.length > 0 ? (
        <div
          id="scrollableCalendarSection"
          className="relative flex max-h-[calc(100dvh-124px)] lg:max-h-[calc(100dvh-58px)] min-w-full grow flex-col items-center justify-start gap-0 overflow-auto"
        >
          <InfiniteScroll
            dataLength={appointmentsOverviewDays.length}
            next={onNext}
            hasMore
            scrollThreshold={0.8}
            scrollableTarget="scrollableCalendarSection"
            loader={
              <div className="flex size-full min-w-full grow flex-col items-center justify-center">
                <Spinner color="fill-sky-600" />
              </div>
            }
            className="m-0 grid size-full min-w-full grow grid-cols-1 gap-2 pb-36 pt-8"
            style={{
              margin: 0,
              marginTop: '8px',
              paddingLeft: '0px',
              paddingRight: '0px',
              overflow: 'hidden',
              alignItems: 'center',
              justifyItems: 'center',
              minWidth: '100%',
            }}
          >
            <div className="flex min-w-full flex-col items-center justify-center pb-6">
              {isLoadingMore ? (
                <div className="flex size-full min-w-full grow flex-col items-center justify-center">
                  <Spinner color="fill-sky-600" />
                </div>
              ) : (
                <button
                  id="appointments_overview_load_more_btn"
                  key="appointments_overview_load_more_btn"
                  aria-label="appointments_overview_load_more_btn"
                  data-test-id="appointments_overview_load_more_btn"
                  type="button"
                  onClick={onLoadMore}
                  className="cursor-pointer rounded-full p-3 text-sm font-bold text-sky-600 hover:bg-sky-600 hover:text-white hover:shadow"
                >
                  {t('static.dashboard.appointments_section.button_label_more')}
                </button>
              )}
            </div>
            <div
              className="relative m-0 flex min-w-full flex-col items-center justify-center gap-0 p-0"
              ref={daysListRef}
            >
              {appointmentsOverviewDays.length > 0 &&
                appointmentsOverviewDays.map((item, index) => (
                  <CalendarDayOverviewDayBlock
                    key={`appointment-overview-block-${item.day}`}
                    index={index}
                    loading={false}
                    data={item}
                    previousDayData={
                      index > 0
                        ? appointmentsOverviewDays[index - 1]
                        : undefined
                    }
                    rootMargin="-52% 0% -48% 0%"
                    onThreshold={() => {
                      debounceVisibleDay(item.timestamp);
                    }}
                    startHour={startEndHours[0]}
                    endHour={startEndHours[1]}
                  />
                ))}
            </div>
          </InfiniteScroll>
        </div>
      ) : (
        <div className="flex h-[calc(100dvh-120px)] min-w-full grow flex-col items-center justify-center">
          <Spinner color="fill-sky-600" />
        </div>
      )}
    </div>
  );
};

export default CalendarDayOverview;
